import { withFormik } from 'formik';
import uniqid from 'uniqid';
import * as Yup from 'yup';
import { resolveDigitalSignatureMessageText } from 'utils/getDigitalSignatureCardCopy';
import excludeAppChannelMembers from '../excludeAppChannelMembers';

class AcknowledgmentSubmit {
  constructor(values, formikBag) {
    this.values = values;
    this.formikBag = formikBag;
  }

  createForm = () => {
    const {
      title,
      body,
      isDraft,
      sentAsChannel,
      digitalSignature,
      fields,
      signingMode,
      attachment,
      template,
      documentFormatTemplate,
      initiativeTag,
    } = this.values;
    const {
      props: {
        message,
        tempAttachment,
        channel,
        authCompany,
        authEmployee,
        authUser,
        channelMembers,
      },
    } = this.formikBag;

    const documentFormatAttachment =
      documentFormatTemplate?.documentSource === 'fixed'
        ? documentFormatTemplate?.templateAttachment
        : null;
    const effectiveAttachment = attachment || documentFormatAttachment;
    const effectiveFields = fields?.length ? fields : documentFormatTemplate?.fields || [];
    const effectiveSigningMode =
      signingMode || documentFormatTemplate?.signingMode || 'onePerMember';

    // Determine which attachment to use
    let attachments = [];
    if (message?.attachments) {
      attachments = message.attachments;
    } else if (effectiveAttachment) {
      attachments = [effectiveAttachment];
    } else if (tempAttachment) {
      attachments = [tempAttachment];
    }

    // Handle template separately - don't mix it with attachments
    let templateData = null;
    if (template) {
      const templateId = template.uuid || template.id || template._id;
      templateData = {
        templateId,
        templateName: template.name || template.title,
        prefillTags: template.prefillTags || [],
      };
      if (!templateId) {
        templateData = null;
      }
    }

    const isDigitalSignatureOn = digitalSignature[0] === 'on';
    const cardTitle =
      (title || '').trim() || effectiveAttachment?.name || documentFormatAttachment?.name || '';
    const messageText = isDigitalSignatureOn
      ? resolveDigitalSignatureMessageText(cardTitle, body)
      : title;

    const form = {
      _id: uniqid(),
      text: messageText,
      title: isDigitalSignatureOn ? cardTitle : title,
      acknowledges: [],
      attachments,
      channel_id: channel._id,
      company_id: authCompany._id,
      previews: {},
      createdAt: Date.now(),
      employee_id: authEmployee?._id,
      user_id: authUser._id,
      isDeletedBy: [],
      isDraft,
      isEdited: false,
      isMandatory: true,
      links: [],
      temp: true,
      seen: false,
      updatedAt: Date.now(),
      senderWasDeleted: false,
      sentAsChannel,
      digitalSignature: digitalSignature[0] === 'on',
      fields: effectiveFields, // Backend will handle provider-specific transformations
      signingMode: effectiveSigningMode,
      initiatives: initiativeTag ? [initiativeTag.id] : [],
    };

    // Only add template if it exists (don't send null)
    if (templateData) {
      form.template = templateData;
    }

    // Add signers array only for oneForAll mode (shared document with multiple signers)
    // For onePerMember mode, don't send signers - backend allows any group member to sign their own copy
    // This avoids issues with pagination limits when fetching group members
    if (
      digitalSignature[0] === 'on' &&
      effectiveSigningMode === 'oneForAll' &&
      channelMembers?.length
    ) {
      form.signers = excludeAppChannelMembers(channelMembers, {
        isAppChannel: !!channel?.isApp,
        channelId: channel?._id,
      })
        .map(member => {
          // Handle both cases: user as object {_id: ...} or user as string ID
          if (typeof member.user === 'object') {
            return member.user?._id;
          }
          return member.user;
        })
        .filter(Boolean);
    }

    return form;
  };

  sendRequest = async form => {
    const { actions, message } = this.formikBag.props;
    if (Array.isArray(form)) {
      // In onePerMember mode, send multiple messages
      await Promise.all(
        form.map(async f => {
          if (message?.isDraft) {
            await actions.messages.update({ ...f, _id: message._id });
          } else {
            await actions.messages.send(f);
          }
        }),
      );
    } else if (message?.isDraft) {
      // In oneForAll mode or when digital signature is disabled
      await actions.messages.update({ ...form, _id: message._id });
    } else {
      await actions.messages.send(form);
    }
  };
}

const formik = withFormik({
  validationSchema: Yup.object().shape({
    title: Yup.string().trim().required('Please enter the title'),
    body: Yup.string(),
  }),

  mapPropsToValues: ({ message, sentAsChannel = false, digitalSignatureMode = false }) => {
    const digitalSignatureOn = Boolean(message?.digitalSignature) || digitalSignatureMode;
    let mappedTitle = message?.title || message?.text || '';
    let mappedBody = '';
    if (digitalSignatureOn) {
      if (message?.title) {
        mappedTitle = message.title;
        if (message?.text && message.text !== message.title) {
          mappedBody = message.text;
        }
      } else {
        mappedTitle = message?.attachments?.[0]?.name || message?.text || '';
      }
    }
    return {
      title: mappedTitle,
      body: mappedBody,
      isDraft: false,
      sentAsChannel,
      digitalSignature: digitalSignatureOn ? ['on'] : [],
      signingMode: message?.signingMode || 'onePerMember',
      fields: message?.fields || [],
      attachment: null, // Will be set via componentDidUpdate
      template: null,
      documentFormatTemplate: null,
      initiativeTag: null,
    };
  },

  enableReinitialize: true,
  handleSubmit: async (values, formikBag) => {
    const { onClose } = formikBag.props;

    const submit = new AcknowledgmentSubmit(values, formikBag);

    const form = submit.createForm();

    await submit.sendRequest(form);

    onClose();
  },
  displayName: 'AcknowledgementForm',
});

export default formik;
